import type { DispositionView, DispositionViewState } from '../model/useDispositionView';

const ITEMS: { label: string; value: DispositionView }[] = [
  { label: 'Tag', value: 'tag' },
  { label: 'Woche', value: 'woche' },
];

/**
 * Selected reads as the arrows' hover state — filled brand blue — so "blue fill" means the same
 * thing everywhere on this row.
 */
const ITEM_CLASS = [
  'flex h-11 items-center justify-center px-5 shrink-0',
  "font-['PT_Sans'] text-[18px] font-bold leading-none",
  'transition duration-200',
].join(' ');

interface Props {
  view: DispositionViewState;
}

/**
 * Tag ↔ Woche, in the toolbar's own button language.
 *
 * Deliberately not `shared/ui`'s `SegmentedControl`. That component is an underlined tab bar — grey
 * label, 2px rule under the active item — which is right where it is used (the task and appointment
 * forms, the settings page) and wrong here: it was the fourth distinct visual language in a row that
 * already had circular arrows, a pill and a status dot. Restyling the shared one would have dragged
 * those three other screens along with it, so this renders its own two buttons instead and leaves
 * `SegmentedControl` alone.
 *
 * `tablist`/`tab` semantics are kept. The buttons switch what fills the main region, which is what
 * the tab roles describe — the change here is paint, not meaning.
 *
 * Renders nothing while a planning or reschedule deep link is open: the week view cannot place a
 * block, so offering the switch there would only lead to a dead end.
 */
export const DispositionViewSwitch = ({ view }: Props) => {
  if (!view.canSwitch) return null;

  return (
    <div
      role="tablist"
      aria-label="Ansicht"
      className="flex items-center overflow-hidden rounded-full bg-white"
    >
      {ITEMS.map((item) => {
        const isActive = view.value === item.value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => view.setValue(item.value)}
            className={[
              ITEM_CLASS,
              isActive
                ? 'bg-[#004494] text-white'
                : 'text-[#004494] hover:bg-[#004494]/10',
            ].join(' ')}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
};
